import { formatQuoteMoney } from './quotePricing';

export interface LedgerPayment {
  type: string;
  status: string;
  verifiedMinor?: number;
}

export interface MoneyFigures {
  totalMinor: number;
  depositRequiredMinor: number;
  receivedMinor: number;
  outstandingMinor: number;
  currentlyDueMinor: number;
  futureBalanceMinor: number;
  secured?: boolean;
  paidInFull?: boolean;
  overdueMinor?: number;
  requestDueOn?: string;
}

export interface LedgerLines {
  grossMinor: number;
  refundedMinor: number;
  netMinor: number;
}

export function ledgerFromPayments(payments: LedgerPayment[], netMinor: number): LedgerLines {
  const refundedMinor = payments.reduce((sum, payment) => {
    if (payment.status !== 'complete' || payment.type !== 'refund') return sum;
    return sum + Math.max(0, Math.round(Number(payment.verifiedMinor) || 0));
  }, 0);
  const net = Math.max(0, Math.round(Number(netMinor) || 0));
  return {
    grossMinor: net + refundedMinor,
    refundedMinor,
    netMinor: net,
  };
}

export function paymentRowLabel(type: string, status: string): string {
  if (type === 'refund') {
    if (status === 'complete') return 'Refunded';
    if (status === 'failed') return 'Refund failed';
    return 'Refund pending';
  }
  if (status === 'complete') return 'Payment received';
  if (status === 'failed') return 'Payment failed';
  if (status === 'cancelled') return 'Payment cancelled';
  if (status === 'rejected') return 'Payment not accepted';
  return 'Waiting for confirmation';
}

export function dueDateLabel(value?: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return '';
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
}

export function accountStatusSentence(figures: MoneyFigures | null | undefined): string {
  if (!figures) return '';
  if (figures.paidInFull) return 'Paid in full.';
  const overdue = Math.max(0, Math.round(figures.overdueMinor || 0));
  if (overdue > 0) {
    const when = dueDateLabel(figures.requestDueOn);
    const amount = formatQuoteMoney(overdue, 'R');
    return when ? `${amount} was due on ${when}.` : `${amount} is overdue.`;
  }
  if (figures.currentlyDueMinor > 0) {
    const when = dueDateLabel(figures.requestDueOn);
    return when ? `Due by ${when}.` : '';
  }
  if (figures.secured && figures.outstandingMinor > 0) {
    return 'The balance is payable on completion of your shoot. You can pay it sooner if you wish.';
  }
  return '';
}
